import { createContext, ReactNode, useEffect, useReducer } from 'react';
import { ILoginForm } from 'utils/login';
import { AuthService } from '_apis_/auth';
import { UserService } from '_apis_/user';
// @types
import { getRoleId, getUserRoles } from 'utils/common';
import { ActionMap, AuthState, JWTContextType } from '../@types/auth';
import { IUser, IUserRoles } from '../@types/user';
import { cleanSession, setSession } from '../utils/jwt';
import { OwnerInterface } from '../@types/owner';
import { OwnerService } from '_apis_/owner';

// ----------------------------------------------------------------------

enum Types {
  Initial = 'INITIALIZE',
  Login = 'LOGIN',
  Logout = 'LOGOUT'
}

type JWTAuthPayload = {
  [Types.Initial]: {
    isAuthenticated: boolean;
    user: null | IUser;
    userRoles: IUserRoles[];
    unassignedOwner: null | OwnerInterface;
  };
  [Types.Login]: {
    user: null | IUser;
    userRoles: IUserRoles[];
    unassignedOwner: null | OwnerInterface;
  };
  [Types.Logout]: undefined;
};

export type JWTActions = ActionMap<JWTAuthPayload>[keyof ActionMap<JWTAuthPayload>];

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  userRoles: [],
  unassignedOwner: null
};

const JWTReducer = (state: AuthState, action: JWTActions) => {
  switch (action.type) {
    case 'INITIALIZE':
      return {
        isAuthenticated: action.payload.isAuthenticated,
        isInitialized: true,
        user: action.payload.user,
        userRoles: action.payload.userRoles,
        unassignedOwner: action.payload.unassignedOwner
      };
    case 'LOGIN':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        userRoles: action.payload.userRoles,
        unassignedOwner: action.payload.unassignedOwner
      };
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        userRoles: [],
        unassignedOwner: null
      };

    default:
      return state;
  }
};

const AuthContext = createContext<JWTContextType | null>(null);

function AuthProvider({ children }: { children: ReactNode }) {
  const authService = new AuthService();

  const userService = new UserService();

  const ownerService = new OwnerService();

  const [state, dispatch] = useReducer(JWTReducer, initialState);

  useEffect(() => {
    initialize();
  }, []);

  const delay = (ms) => new Promise((res) => setTimeout(res, ms));

  const userDetails = async (email: string) => {
    if (email) {
      const userResponse = await userService.getUserDetails(email);
      if (
        userResponse &&
        userResponse.status >= 200 &&
        userResponse.status <= 299 &&
        userResponse.data
      ) {
        const { data } = userResponse;
        const user: IUser = { ...data };
        return user;
      }
      return null;
    } else {
      dispatch({
        type: Types.Initial,
        payload: {
          isAuthenticated: false,
          user: null,
          userRoles: [],
          unassignedOwner: null
        }
      });
    }
  };

  const getUnassignedOwner = async () => {
    const response = await ownerService.getUnassignedOwner();
    const { data } = response;
    return data;
  };

  const initialize = async () => {
    try {
      const { userInfo } = authService;
      //get userInfo form localStorage
      const { email } = userInfo;
      const user = await userDetails(email);
      if (user) {
        const userRoles = await getUserRoles();
        Object.assign(user, { userRoleId: getRoleId(user.roles[0], userRoles) });
        localStorage.setItem('UserInfo', JSON.stringify({ ...user }) || '');
        const unassignedOwner: OwnerInterface = await getUnassignedOwner();
        dispatch({
          type: Types.Initial,
          payload: {
            isAuthenticated: true,
            user,
            userRoles,
            unassignedOwner
          }
        });
      } else {
        dispatch({
          type: Types.Initial,
          payload: {
            isAuthenticated: false,
            user: null,
            userRoles: [],
            unassignedOwner: null
          }
        });
      }
    } catch (error) {
      dispatch({
        type: Types.Initial,
        payload: {
          isAuthenticated: false,
          user: null,
          userRoles: [],
          unassignedOwner: null
        }
      });
    }
  };

  const login = async (loginData: ILoginForm) => {
    const { email } = loginData;
    const loginResponse = await authService.login(loginData);
    const { headers } = loginResponse;
    const authorization = headers['authorization'];
    const refreshToken = headers['refresh-token'];
    if (authorization && refreshToken) {
      const authArray = authorization.split(' ');
      const authToken = authArray[1];
      setSession(authToken, refreshToken);
    }
    await delay(1000);
    processUserDetails(email);
  };

  const processUserDetails = async (email: any) => {
    const user = await userDetails(email);
    if (user) {
      const userRoles = await getUserRoles();
      Object.assign(user, { userRoleId: getRoleId(user.roles[0], userRoles) });
      localStorage.setItem('UserInfo', JSON.stringify({ ...user }) || '');
      const unassignedOwner: OwnerInterface = await getUnassignedOwner();
      dispatch({
        type: Types.Login,
        payload: {
          user,
          userRoles,
          unassignedOwner
        }
      });
    }
  };

  const logout = async (userId: number) => {
    const logoutResponse = await authService.logout(userId);
    if (logoutResponse && logoutResponse.status >= 200 && logoutResponse.status <= 299) {
      cleanSession();
      dispatch({ type: Types.Logout });
    }
  };

  const updateProfile = () => {};

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        updateProfile
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
